import type { EventType } from '../composites/eventBooking';
import { toE164 } from '../records/phone';
import { validateEmail } from '../validators';
import { addCalendarDays, instantFromWallClock } from '../zonedTime';
import type { EventReminderDraft } from './eventReminder';

/**
 * The Add/Edit Event modal (piece 5; decisions 0118, 0134, 0180). It is the
 * only event editor in the plan, so every rule about what an event may say is
 * kept here or nowhere.
 */

/**
 * The three types the modal offers. PTO is not among them: an approved PTO
 * event is written by the HR approval and never typed (decision 0118), and a
 * WorkOrderBlock is written by the Work Order.
 */
export const EVENT_MODAL_TYPES = ['Physical', 'Online', 'Phone'] as const;
export type EventModalType = (typeof EVENT_MODAL_TYPES)[number];

export function offeredEventTypes(): readonly EventModalType[] {
  return EVENT_MODAL_TYPES;
}

/** The Event Type control reveals exactly one further field, never two and never none. */
export type RevealedEventField = 'place' | 'meetingLink' | 'callNumber';

export function revealedField(type: EventModalType): RevealedEventField {
  switch (type) {
    case 'Online':
      return 'meetingLink';
    case 'Phone':
      return 'callNumber';
    case 'Physical':
      return 'place';
  }
}

/**
 * The place is one control over both Sites and Locations, not two (decision
 * 0180). A chosen place clears the other key, so no event ever carries both.
 */
export interface PlaceChoice {
  siteId: string | null;
  locationId: string | null;
}

export const noPlace = (): PlaceChoice => ({ siteId: null, locationId: null });

/** How many places a search answers with; the field is a type-ahead, not a list (decision 0330). */
export const PLACE_SEARCH_LIMIT = 20;

/**
 * Whether typed text matches a place (decision 0330): every word typed is
 * found, case-blind, somewhere in the fields the place is known by — a Site's
 * name, its Location's street and town, the Company occupying it. One matcher
 * serves the server's search and the modal's own list, so the two agree.
 */
export function placeMatches(query: string, fields: readonly (string | null | undefined)[]): boolean {
  const words = query.toLowerCase().split(/\s+/).filter((word) => word !== '');
  if (words.length === 0) return true;
  const haystack = fields.filter((field): field is string => typeof field === 'string' && field !== '').join(' ').toLowerCase();
  return words.every((word) => haystack.includes(word));
}

export function chooseSite(siteId: string): PlaceChoice {
  return { siteId, locationId: null };
}

export function chooseLocation(locationId: string): PlaceChoice {
  return { siteId: null, locationId };
}

/** A Location the Company-to-occupancy cascade resolves to more than one Site asks which is meant. */
export function needsWhichSite(resolvedSiteIds: readonly string[]): boolean {
  return resolvedSiteIds.length > 1;
}

/** The answer to that question writes `siteId`. */
export function answerWhichSite(siteId: string): PlaceChoice {
  return chooseSite(siteId);
}

/**
 * The zone is stored as an IANA name and never derived at read time: from the
 * chosen place's zone on a Physical event, and from the organizer's own zone
 * otherwise. The place-derived half becomes live once Company, Person,
 * Location lands; phase 1 falls back to the organizer where no place answers.
 */
export function timeZoneFor(type: EventModalType, placeZone: string | null | undefined, organizerZone: string): string {
  return type === 'Physical' && placeZone ? placeZone : organizerZone;
}

export type Recurrence = 'None' | 'Daily' | 'Weekly' | 'Monthly';
export const RECURRENCES: readonly Recurrence[] = ['None', 'Daily', 'Weekly', 'Monthly'];

/** None writes no rule; the other three write one, with the optional end date on it. */
export function recurrenceRule(recurrence: Recurrence, endDate: string | null, zone: string): string | null {
  if (recurrence === 'None') return null;
  const freq = recurrence === 'Daily' ? 'DAILY' : recurrence === 'Weekly' ? 'WEEKLY' : 'MONTHLY';
  return `FREQ=${freq}${endDate ? `;UNTIL=${untilFor(endDate, zone)}` : ''}`;
}

/**
 * 💰 The end date is the last day the series runs, read in the event's own
 * zone, so UNTIL is the last instant of that day and not its midnight. A bare
 * `UNTIL=20261231` is read as midnight UTC, which falls before a morning
 * occurrence anywhere west of it: a daily series ending on the 31st lost the
 * 31st, silently, and the reader had no way to see it (found while building,
 * 2026-09-09). RFC 5545 also requires a DATE-TIME UNTIL beside a DATE-TIME
 * start, which the bare form is not. 💰 The last instant is midnight of the day after the end date less a second rather
 * than the same day plus 24 hours, because a day daylight saving changes is 23 or 25 hours long and the shorter one
 * would spill into the following day.
 */
function untilFor(endDate: string, zone: string): string {
  const lastInstant = new Date(instantFromWallClock(`${addCalendarDays(endDate, 1)}T00:00:00`, zone).getTime() - 1000);
  return `${lastInstant.toISOString().replace(/[-:]/g, '').slice(0, 15)}Z`;
}

/** The three edit scopes of Foundational data types and the seams. */
export type SeriesScope = 'thisOne' | 'allOfThem' | 'thisAndEveryFuture';
export const SERIES_SCOPES: readonly SeriesScope[] = ['thisOne', 'allOfThem', 'thisAndEveryFuture'];

/**
 * Saving or deleting a row carrying a parent or a rule asks first. The modal
 * passes the answer on and applies none of the three itself; unasked, one
 * occurrence and a whole series go through the same code path.
 */
export function needsSeriesScope(row: { parentEventId?: string | null; recurrenceRule?: string | null }): boolean {
  return Boolean(row.parentEventId) || Boolean(row.recurrenceRule);
}

export type SiteRequirementKind = 'Equipment' | 'Verified';

export interface SiteRequirementRow {
  id: string;
  name: string;
  kind: SiteRequirementKind;
  /** The RequirementType a Verified row points at, matched against a completion. */
  requirementTypeId: string | null;
}

export interface SitePolicyRow {
  id: string;
  title: string;
}

/** What `resolveSiteCascade(site)` answers, declared in Company, Person, Location. */
export interface ResolvedSite {
  requirements: readonly SiteRequirementRow[];
  policies: readonly SitePolicyRow[];
}

export type ResolveSiteCascade = (siteId: string) => ResolvedSite | null;

export interface InternalAttendee {
  personId: string;
  name: string;
  /** The RequirementType ids this person holds, and when each expires. */
  completions: readonly { requirementTypeId: string; expiresOn: string | null }[];
}

export interface VerifiedRow {
  requirement: SiteRequirementRow;
  attendees: readonly { personId: string; name: string; met: boolean; expiresOn: string | null }[];
}

export interface SiteRequirementsBand {
  /** The Equipment-kind rows: what to bring. */
  bring: readonly SiteRequirementRow[];
  /** The Verified-kind rows, checked against each internal attendee. */
  verified: readonly VerifiedRow[];
  policies: readonly SitePolicyRow[];
}

/**
 * The band shows three things: what to bring, what each attendee must already
 * hold, and the site's own policies. It never blocks Save — the gate belongs
 * to the Work Order, the only context where the system knows the visit is work
 * rather than a meeting in the front office.
 */
export const BAND_BLOCKS_SAVE = false;

export function siteRequirementsBand(resolved: ResolvedSite, attendees: readonly InternalAttendee[]): SiteRequirementsBand {
  const bring = resolved.requirements.filter((r) => r.kind === 'Equipment');
  const verified = resolved.requirements
    .filter((r) => r.kind === 'Verified')
    .map((requirement) => ({
      requirement,
      attendees: attendees.map((person) => {
        const held = person.completions.find((c) => c.requirementTypeId === requirement.requirementTypeId);
        return { personId: person.personId, name: person.name, met: Boolean(held), expiresOn: held?.expiresOn ?? null };
      }),
    }));
  return { bring, verified, policies: resolved.policies };
}

/**
 * A site with no resolved requirements and no policies renders no band at all,
 * and an Online or Phone event never resolves one. A Location with no Site
 * behind it is a valid place and shows no band, because there is no site to
 * have requirements.
 */
export function rendersBand(type: EventModalType, resolved: ResolvedSite | null): resolved is ResolvedSite {
  if (type !== 'Physical' || !resolved) return false;
  return resolved.requirements.length > 0 || resolved.policies.length > 0;
}

/**
 * 🔒 A guest who is not a Person record: a name, and the address or number the
 * invitation would reach them on (decision 0596). Many guests stand on one
 * event, which is what the attendee table has always held; the modal offered
 * one free-text box and wrote one row.
 *
 * 🔨 The name is what identifies the guest, so two rows with one name are one
 * guest. 💰 The RSVP is carried and drawn, never typed here: nothing in the
 * application asks a person's RSVP by hand, and a control for a guest's alone
 * would be a second mechanism for the same fact (found while building,
 * 2026-09-20).
 */
export interface OutsideGuest {
  name: string;
  email: string | null;
  phone: string | null;
  /** The answer the row holds, drawn beside the guest on an existing event; NeedsAction until they answer. */
  attending?: 'NeedsAction' | 'Accepted' | 'Declined' | 'Tentative';
}

export const emptyOutsideGuest = (): OutsideGuest => ({ name: '', email: null, phone: null });

/** Which field stops a guest being added: a guest carries a name, and an address that is given is an address. */
export type OutsideGuestProblem = 'name' | 'email' | 'phone' | 'duplicate';

/** 🔨 The same two validators the server refuses on, so the window and the route never disagree about a guest's address or number. */
export function outsideGuestProblem(guest: OutsideGuest, held: readonly OutsideGuest[] = []): OutsideGuestProblem | null {
  const name = guest.name.trim();
  if (name === '') return 'name';
  const email = (guest.email ?? '').trim();
  if (email !== '' && !validateEmail(email, 'US').ok) return 'email';
  const phone = (guest.phone ?? '').trim();
  if (phone !== '') {
    try {
      toE164(phone, 'US');
    } catch {
      return 'phone';
    }
  }
  if (held.some((row) => row.name.trim().toLowerCase() === name.toLowerCase())) return 'duplicate';
  return null;
}

/** The guest added to the list, trimmed, with a blank address or number held as nothing rather than as an empty string. */
export function addOutsideGuest(held: readonly OutsideGuest[], guest: OutsideGuest): OutsideGuest[] {
  const trimmed: OutsideGuest = {
    name: guest.name.trim(),
    email: (guest.email ?? '').trim() === '' ? null : (guest.email ?? '').trim(),
    phone: (guest.phone ?? '').trim() === '' ? null : (guest.phone ?? '').trim(),
  };
  return [...held, trimmed];
}

export function removeOutsideGuest(held: readonly OutsideGuest[], name: string): OutsideGuest[] {
  return held.filter((row) => row.name !== name);
}

/** How a guest reads on their chip: the name, then whichever of the two ways to reach them is held. */
export function outsideGuestLabel(guest: OutsideGuest): string {
  return [guest.name, guest.email, guest.phone].filter((part) => part !== null && part !== '').join(' · ');
}

export interface EventDraft {
  subject: string;
  taggedRecordType: string | null;
  taggedRecordId: string | null;
  /** The tagged record by name, so the Linked To field reads it on an existing event; never stored. */
  taggedRecordLabel?: string | null;
  eventType: EventModalType;
  place: PlaceChoice;
  meetingLink: string | null;
  callNumber: string | null;
  /** The digits a caller types after the bridge answers; beside the number, never inside it. */
  callNumberExtension: string | null;
  startsAt: string;
  endsAt: string;
  isAllDay: boolean;
  timeZone: string;
  ownerPersonId: string;
  recurrence: Recurrence;
  recurrenceEndDate: string | null;
  attendeePersonIds: readonly string[];
  /** 🔨 Many guests, each a name with an address and a number beside it (decision 0596). */
  outsideContacts: readonly OutsideGuest[];
  notes: string | null;
  /**
   * The viewer's own reminder (decision 1081): null is none, and absent leaves
   * whatever is stored as it stands, so a host that never read it cannot clear it.
   */
  reminder?: EventReminderDraft | null;
  /**
   * The event's version as the window read it (decision 10670): sent, the save compares it in the write's predicate
   * and a stale one is refused; absent, the save writes unconditionally (decision 10452).
   */
  version?: number | null;
}

export function emptyEvent(ownerPersonId: string, organizerZone: string): EventDraft {
  return {
    subject: '',
    taggedRecordType: null,
    taggedRecordId: null,
    eventType: 'Physical',
    place: noPlace(),
    meetingLink: null,
    callNumber: null,
    callNumberExtension: null,
    startsAt: '',
    endsAt: '',
    isAllDay: false,
    timeZone: organizerZone,
    ownerPersonId,
    recurrence: 'None',
    recurrenceEndDate: null,
    attendeePersonIds: [],
    outsideContacts: [],
    notes: null,
  };
}

/**
 * 🔒 The attendees a new event opens with: the person creating it, and the
 * record's own person where the modal was opened from one (product owner,
 * 2026-09-11 and 2026-09-20). It is asked on the OPEN, so the creator reads
 * their own name in the Attendees chips before they save rather than finding it
 * added afterwards. 💰 The calendar's own + Add Event opened with no attendees
 * at all and the server added the owner on save, so one modal behaved two ways
 * depending on which screen opened it (product owner, 2026-09-20).
 */
export function startingAttendees(ownerPersonId: string, associated?: { personId: string } | null): string[] {
  return [...new Set([ownerPersonId, associated?.personId ?? ''].filter((id) => id !== ''))];
}

/** Title, Start and End are required; nothing else on the modal is. */
export function eventProblems(draft: EventDraft): { field: 'subject' | 'startsAt' | 'endsAt' }[] {
  const problems: { field: 'subject' | 'startsAt' | 'endsAt' }[] = [];
  if (draft.subject.trim() === '') problems.push({ field: 'subject' });
  if (draft.startsAt === '') problems.push({ field: 'startsAt' });
  if (draft.endsAt === '') problems.push({ field: 'endsAt' });
  return problems;
}

/** The modal's own type widened to the calendar's, where PTO and the block also live. */
export function asCalendarEventType(type: EventModalType | 'WorkOrderBlock'): EventType {
  return type;
}

/**
 * 🔒 A draft the server writes (decision 2982): the modal's own, with the
 * system kind a job's booking carries beside the three a person adds.
 */
export type CalendarEventDraft = Omit<EventDraft, 'eventType'> & { eventType: EventModalType | 'WorkOrderBlock' };
